import { colors } from '../theme/colors';

export enum StudentClassificationStatus {
  LEAD = 'lead',
  PRE_STUDENT = 'pre_student',
  PRE_STUDENT_INACTIVE = 'pre_student_inactive',
  NEW_STUDENT = 'new_student',
  OLD_STUDENT = 'old_student',
}

export interface StudentClassification {
  status: StudentClassificationStatus;
  label: string;
}

const CLASSIFICATION_LABELS: Record<StudentClassificationStatus, string> = {
  [StudentClassificationStatus.LEAD]: 'Lead',
  [StudentClassificationStatus.PRE_STUDENT]: 'Pré-aluna',
  [StudentClassificationStatus.PRE_STUDENT_INACTIVE]: 'Pré-aluna inativa',
  [StudentClassificationStatus.NEW_STUDENT]: 'Aluna Nova',
  [StudentClassificationStatus.OLD_STUDENT]: 'Aluna Antiga',
};

export function mapStudentClassificationFromColumn(
  status: string | null | undefined,
): StudentClassification | null {
  if (!status) {
    return null;
  }

  if (
    status === StudentClassificationStatus.LEAD
    || status === StudentClassificationStatus.PRE_STUDENT
    || status === StudentClassificationStatus.PRE_STUDENT_INACTIVE
    || status === StudentClassificationStatus.NEW_STUDENT
    || status === StudentClassificationStatus.OLD_STUDENT
  ) {
    return {
      status,
      label: CLASSIFICATION_LABELS[status],
    };
  }

  return null;
}

export function mapStudentClassificationFromJson(
  value: unknown,
): StudentClassification | null {
  if (typeof value === 'string') {
    return mapStudentClassificationFromColumn(value);
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  return mapStudentClassificationFromColumn(
    typeof record.status === 'string' ? record.status : null,
  );
}

export const shouldDisplayStudentClassificationBadge = (
  classification: StudentClassification | null | undefined,
): classification is StudentClassification => (
  Boolean(classification)
  && classification?.status !== StudentClassificationStatus.OLD_STUDENT
);

export const getStudentClassificationBadgeVariant = (
  classification: StudentClassification,
): 'success' | 'info' | 'warning' => {
  if (classification.status === StudentClassificationStatus.LEAD) {
    return 'success';
  }

  if (
    classification.status === StudentClassificationStatus.PRE_STUDENT
    || classification.status === StudentClassificationStatus.PRE_STUDENT_INACTIVE
  ) {
    return 'warning';
  }

  if (classification.status === StudentClassificationStatus.NEW_STUDENT) {
    return 'info';
  }

  return 'warning';
};

/** Soft badge colors for ClassificationBadge. Lead tokens live in theme/colors. */
export function getStudentClassificationBadgeColors(
  classification: StudentClassification,
): { background: string; text: string } {
  if (classification.status === StudentClassificationStatus.LEAD) {
    return { background: colors.classificationLeadBg, text: colors.classificationLeadText };
  }

  if (
    classification.status === StudentClassificationStatus.PRE_STUDENT
    || classification.status === StudentClassificationStatus.PRE_STUDENT_INACTIVE
  ) {
    return { background: 'rgba(245, 158, 11, 0.15)', text: '#92400e' };
  }

  if (classification.status === StudentClassificationStatus.NEW_STUDENT) {
    return { background: 'rgba(59, 130, 246, 0.15)', text: '#1d4ed8' };
  }

  return { background: 'rgba(245, 158, 11, 0.15)', text: '#92400e' };
}
